import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Store, Send, CheckCircle2, Mail, Phone, MapPin, FileText, Building2, ArrowLeft, User, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function RestaurantSignup() {
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        address: "",
        vat_number: "",
        phone: "",
        owner_email: "",
        description: "",
        username: "",
        password: "",
        password_confirm: "",
        privacy_accepted: false,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.privacy_accepted) {
            toast.error("Devi accettare l'informativa sulla privacy");
            return;
        }
        if (form.password !== form.password_confirm) {
            toast.error("Le password non coincidono");
            return;
        }
        setSubmitting(true);
        try {
            const { password_confirm, ...payload } = form;
            await api.post("/restaurants/signup", payload);
            setSubmitted(true);
            toast.success("Iscrizione inviata!");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore durante l'iscrizione");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-ink grain pt-24 pb-16 px-6 md:px-10 flex items-center justify-center" data-testid="signup-success-page">
                <div className="max-w-xl w-full bg-white/5 backdrop-blur-md border border-emerald-500/30 rounded-sm p-10 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 mb-6">
                        <CheckCircle2 size={32} className="text-emerald-300" strokeWidth={1.5} />
                    </div>
                    <h2 className="font-serif text-3xl text-white mb-3">Richiesta inviata</h2>
                    <p className="text-gray-300 leading-relaxed mb-2">
                        Grazie per esserti iscritto a <span className="text-gold">Prenova by Renova360</span>.
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed mb-8">
                        Il nostro team valuterà la tua richiesta entro 24-48 ore. Quando il tuo account sarà approvato, riceverai una email all'indirizzo <span className="text-white">{form.owner_email}</span> e potrai accedere subito con username <span className="text-gold">{form.username}</span> e la password che hai scelto.
                    </p>
                    <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-gold hover:text-gold-light text-sm tracking-widest uppercase" data-testid="signup-back-home">
                        <ArrowLeft size={14} /> Torna alla home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink grain pt-24 pb-16 px-6 md:px-10" data-testid="signup-restaurateur-page">
            <div className="max-w-2xl mx-auto">
                <Link to="/" className="inline-flex items-center gap-2 text-gold hover:text-gold-light text-xs tracking-widest uppercase mb-6">
                    <ArrowLeft size={14} /> Torna alla home
                </Link>
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-px w-12 bg-gold" />
                    <span className="text-gold text-xs tracking-[0.4em] uppercase">Iscrizione Ristoratore</span>
                </div>
                <h1 className="font-serif text-4xl md:text-5xl text-white font-light leading-tight">
                    Porta il tuo <span className="italic text-gold">ristorante</span> su Prenova by Renova360
                </h1>
                <p className="mt-5 text-gray-400 max-w-xl">
                    Compila il modulo qui sotto. Dopo l'approvazione, riceverai via email il link per attivare il tuo pannello di gestione e iniziare a ricevere prenotazioni.
                </p>

                <form onSubmit={handleSubmit} className="mt-10 space-y-5 bg-white/5 backdrop-blur-md border border-white/10 rounded-sm p-7">
                    <Field label="Nome del ristorante" icon={Store} required>
                        <input
                            type="text" required minLength={2} maxLength={120}
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Es. Trattoria del Vicolo"
                            data-testid="signup-name"
                            className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                        />
                    </Field>

                    <Field label="Indirizzo esatto" icon={MapPin} required>
                        <input
                            type="text" required minLength={5} maxLength={250}
                            value={form.address}
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                            placeholder="Via, civico, CAP, città"
                            data-testid="signup-address"
                            className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                        />
                    </Field>

                    <Field label="Partita IVA" icon={FileText} required>
                        <input
                            type="text" required minLength={5} maxLength={30}
                            value={form.vat_number}
                            onChange={(e) => setForm({ ...form, vat_number: e.target.value.toUpperCase() })}
                            placeholder="IT01234567890"
                            data-testid="signup-vat"
                            className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm uppercase"
                        />
                    </Field>

                    <Field label="Telefono ristorante" icon={Phone} required>
                        <input
                            type="tel" required minLength={5} maxLength={30}
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="+39 081 1234567"
                            data-testid="signup-phone"
                            className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                        />
                    </Field>

                    <Field label="Email del titolare" icon={Mail} required>
                        <input
                            type="email" required
                            value={form.owner_email}
                            onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                            placeholder="titolare@ristorante.it"
                            data-testid="signup-email"
                            className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                        />
                    </Field>

                    <Field label="Note (opzionale)" icon={Building2}>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Presentati al nostro team: tipologia di cucina, anni di attività, posti a sedere..."
                            rows={3} maxLength={1000}
                            data-testid="signup-description"
                            className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm resize-none"
                        />
                    </Field>

                    <div className="border-t border-white/10 pt-6 mt-2">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Credenziali di accesso</div>

                        <div className="space-y-4">
                            <Field label="Username" icon={User} required>
                                <input
                                    type="text" required minLength={3} maxLength={40}
                                    value={form.username}
                                    onChange={(e) => setForm({ ...form, username: e.target.value.trim().toLowerCase() })}
                                    placeholder="es. trattoriadelvicolo"
                                    data-testid="signup-username"
                                    autoComplete="username"
                                    className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                                />
                            </Field>

                            <Field label="Password (min 8 caratteri)" icon={Lock} required>
                                <input
                                    type="password" required minLength={8} maxLength={80}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    data-testid="signup-password"
                                    autoComplete="new-password"
                                    className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                                />
                            </Field>

                            <Field label="Conferma password" icon={Lock} required>
                                <input
                                    type="password" required minLength={8} maxLength={80}
                                    value={form.password_confirm}
                                    onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                                    data-testid="signup-password-confirm"
                                    autoComplete="new-password"
                                    className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                                />
                            </Field>
                            {form.password && form.password_confirm && form.password !== form.password_confirm && (
                                <p className="text-xs text-rose-400" data-testid="signup-password-mismatch">Le password non coincidono</p>
                            )}
                        </div>
                    </div>

                    <label className="flex items-start gap-3 mt-2 cursor-pointer text-xs text-gray-300 leading-relaxed" data-testid="signup-privacy-row">
                        <input
                            type="checkbox" required
                            checked={form.privacy_accepted}
                            onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
                            data-testid="signup-privacy"
                            className="mt-1 w-4 h-4 accent-gold cursor-pointer"
                        />
                        <span>
                            Ho letto e accetto l'<Link to="/privacy" className="text-gold underline hover:text-gold-light" target="_blank">Informativa sulla privacy</Link> e i <Link to="/cookie-policy" className="text-gold underline hover:text-gold-light" target="_blank">cookie</Link>. Acconsento al trattamento dei dati per la gestione della richiesta di iscrizione (art. 13 GDPR 2016/679).
                        </span>
                    </label>

                    <button
                        type="submit"
                        disabled={submitting || !form.privacy_accepted}
                        data-testid="signup-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors tracking-widest uppercase text-sm disabled:opacity-40 disabled:cursor-not-allowed mt-2">
                        {submitting ? "Invio in corso..." : <>Invia richiesta <Send size={15} /></>}
                    </button>
                    <p className="text-[11px] text-gray-500 text-center">Nessun pagamento richiesto. Riceverai una conferma via email.</p>
                </form>
            </div>
        </div>
    );
}

function Field({ label, icon: Icon, required, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1.5">
                {label} {required && <span className="text-rose-400">*</span>}
            </label>
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-3 text-gold/70" size={16} strokeWidth={1.5} />}
                {children}
            </div>
        </div>
    );
}
